import { FormControlLabel, Switch, TextField, Typography } from '@mui/material'
import { SaveButton } from 'components/Common/Button/SaveButton'
import { ApiErrors } from 'customHooks/useAppController'
import { ConfirmationDialogProps } from 'features/ConfirmationDialog/ConfirmationDialog'
import { useDialogManager } from 'features/DialogManager/useDialogManager'
import { useCallback, useEffect, useState } from 'react'
import {
  ManagementPartyDto,
  PartySCSNDetailsDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { makeStyles } from 'tss-react/mui'

type Props = {
  party: ManagementPartyDto
  onChange: <K extends keyof ManagementPartyDto>(
    key: K,
    value: ManagementPartyDto[K],
  ) => void
  onSaveSCSNData: (scsnDetails: PartySCSNDetailsDto) => void
  onProvision: (scsnDetails: PartySCSNDetailsDto) => void
  onDeProvision: () => void
  loading: Record<string, boolean>
  errors: Record<string, ApiErrors>
}

const useStyles = makeStyles({ name: 'scsn-provisioning' })((theme) => ({
  fieldset: {
    marginTop: '1rem',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    gap: '1rem',
  },
  switcher: {
    justifyContent: 'center',
  },
  switches: {
    display: 'flex',
    gap: '2rem',
    justifyContent: 'center',
  },
  buttons: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
  },
}))

export const SCSNProvisioningForm = (props: Props) => {
  const { classes } = useStyles()
  const [changedSCSN, setChangedSCSN] = useState<PartySCSNDetailsDto>(
    props.party?.scsnDetails,
  )

  useEffect(() => {
    setChangedSCSN(props.party?.scsnDetails)
  }, [props.party?.scsnDetails])

  const handleChange = useCallback(
    <k extends keyof PartySCSNDetailsDto>(
      key: k,
      value: PartySCSNDetailsDto[k],
    ) => {
      const newSCSN = {
        ...(changedSCSN || {
          isEnabled: false,
          isProvisioned: false,
          partyName: null,
          partyLegalEntityID: null,
          partyTaxID: null,
          partyIdentification: null,
        }),
        ...{ [key]: value },
      }

      setChangedSCSN(newSCSN)
    },
    [changedSCSN],
  )

  const { openDialog } = useDialogManager()

  const confirmation = (callback: () => void) => {
    openDialog('ConfirmationDialog', {
      title: 'SCSN',
      message: 'Are you sure you want to continue?',
      onConfirm: () => {
        callback()
      },
    } as ConfirmationDialogProps)
  }

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    props.onChange('scsnDetails', changedSCSN)

    const submitter = e.nativeEvent['submitter'] as HTMLButtonElement

    switch (submitter.name) {
      case 'save': {
        props.onSaveSCSNData(changedSCSN)
        break
      }
      case 'provision': {
        confirmation(() => props.onProvision(changedSCSN))
        break
      }
    }
  }

  if (!props.party) {
    return null
  }

  return (
    <fieldset className={classes.fieldset}>
      <legend>SCSN Details</legend>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          handleSubmit(e)
        }}
        className={classes.form}
      >
        <div className={classes.switches}>
          <FormControlLabel
            control={
              <Switch
                disabled
                size="small"
                checked={changedSCSN?.isEnabled}
                onChange={(_, checked) => {
                  handleChange('isEnabled', checked)
                }}
              />
            }
            label={'isEnabled'}
            labelPlacement="end"
            className={classes.switcher}
          />
          <FormControlLabel
            control={
              <Switch
                disabled
                size="small"
                checked={changedSCSN?.isProvisioned}
                onChange={(_, checked) => {
                  handleChange('isEnabled', checked)
                }}
              />
            }
            label={'isProvisioned'}
            labelPlacement="end"
            className={classes.switcher}
          />
        </div>
        <TextField
          required
          value={changedSCSN?.partyName}
          onChange={(e) => {
            handleChange('partyName', e.currentTarget.value)
          }}
          label={'party name'}
        />
        <TextField
          required
          value={changedSCSN?.partyLegalEntityID}
          onChange={(e) => {
            handleChange('partyLegalEntityID', e.currentTarget.value)
          }}
          label={'partyLegalEntityID'}
        />

        <TextField
          required
          value={changedSCSN?.partyTaxID}
          onChange={(e) => {
            handleChange('partyTaxID', e.currentTarget.value)
          }}
          label={'partyTaxID'}
        />

        <TextField
          value={changedSCSN?.partyIdentification ?? ''}
          disabled
          label={'partyIdentification'}
        />
        {props.errors?.['SCSNManagementAPI-ProvisionSCSN'] && (
          <>
            <Typography color="error" variant="caption" fontWeight={'bold'}>
              {props.errors['SCSNManagementAPI-ProvisionSCSN'].message ||
                'An error occurred while provisioning SCSN.'}
            </Typography>
          </>
        )}
        <div className={classes.buttons}>
          <>
            {changedSCSN?.isProvisioned ? (
              <SaveButton
                name={'deprovision'}
                type="button"
                onClick={() => {
                  confirmation(props.onDeProvision)
                }}
                color="error"
                variant="contained"
                saving={props.loading['SCSNManagementAPI-DeProvisionSCSN']}
              >
                deprovision
              </SaveButton>
            ) : (
              <SaveButton
                name="provision"
                type={'submit'}
                color="primary"
                variant="contained"
                saving={props.loading['SCSNManagementAPI-ProvisionSCSN']}
              >
                provision
              </SaveButton>
            )}
          </>
        </div>
      </form>
    </fieldset>
  )
}
